import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import FaqAccordion from "@/components/FaqAccordion";
import CTASection from "@/components/CTASection";
import { useJsonLd } from "@/hooks/useJsonLd";
import { FAQ_CATEGORIES } from "@/data/faqs";

const Faq = () => {
  const [activeCat, setActiveCat] = useState(FAQ_CATEGORIES[0].id);

  useEffect(() => {
    document.title = "FAQs — Click World Online";
  }, []);

  const allFaqs = FAQ_CATEGORIES.flatMap((c) => c.faqs);
  useJsonLd("faq-jsonld", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allFaqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });

  const jump = (id) => {
    setActiveCat(id);
    document.getElementById(`faq-cat-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <PageHeader
        eyebrow="FAQs"
        title={<>Questions, <span className="text-electric">answered honestly.</span></>}
        text="Everything businesses usually ask before working with us — about websites, SEO, advertising, Google Business Profile, 360° tours, pricing and support."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "FAQs" }]}
      />

      <section className="bg-navy-deep" data-testid="faq-page-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="lg:grid lg:grid-cols-12 lg:gap-14">
            <aside className="lg:col-span-4 mb-12 lg:mb-0">
              <div className="lg:sticky lg:top-28">
                <p className="text-xs uppercase tracking-[0.25em] text-mist/40 font-semibold mb-5">Jump to a topic</p>
                <div className="flex flex-wrap lg:flex-col gap-2" data-testid="faq-category-nav">
                  {FAQ_CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => jump(c.id)}
                      data-testid={`faq-cat-${c.id}`}
                      className={`text-left text-sm font-medium rounded-full lg:rounded-xl px-4 py-2.5 transition-colors duration-200 border ${
                        activeCat === c.id
                          ? "border-electric/50 text-electric bg-electric/5"
                          : "border-white/10 text-mist/60 hover:text-mist hover:border-white/25"
                      }`}
                    >
                      {c.title}
                      <span className="ml-2 text-xs opacity-50">{c.faqs.length}</span>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            <div className="lg:col-span-8 flex flex-col gap-16">
              {FAQ_CATEGORIES.map((c, ci) => (
                <div key={c.id} id={`faq-cat-${c.id}`} className="scroll-mt-28" data-testid={`faq-category-section-${c.id}`}>
                  <Reveal>
                    <h2 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-mist mb-6">
                      <span className="text-electric mr-3">{String(ci + 1).padStart(2, "0")}</span>
                      {c.title}
                    </h2>
                  </Reveal>
                  <FaqAccordion items={c.faqs} dark idPrefix={`faq-${c.id}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection title="Still have a question?" text="Ask us directly — call, WhatsApp or send an enquiry. Real answers from the people who'll actually do the work." />
    </>
  );
};

export default Faq;
